import { useState } from "react"
import { ChevronRight, Grid, List, MoreVertical, File, Folder, Image as ImageIcon } from "lucide-react"
import { Button } from "@/components/ui/button"
import { DriveItem } from "@/lib/api/knowledge-base"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { format, formatDistanceToNow } from "date-fns"
import { cn } from "@/lib/utils"

interface FileBrowserProps {
  items: DriveItem[]
  currentPath: string
  view: 'grid' | 'list'
  isLoading?: boolean
  onPathChange: (path: string) => void
  onViewChange: (view: 'grid' | 'list') => void
  onItemClick: (item: DriveItem) => void
  onMove?: (item: DriveItem) => void
  onGetDetails?: (item: DriveItem) => void
  onCreateFolder?: () => void
}

const toTitleCase = (str: string) => {
  return str.split(/[_\s]/).map(word => 
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ')
}

export function FileBrowser({
  items,
  currentPath,
  view,
  isLoading,
  onPathChange,
  onViewChange,
  onItemClick,
  onMove,
  onGetDetails,
  onCreateFolder
}: FileBrowserProps) {
  const [selectedItems, setSelectedItems] = useState<DriveItem[]>([])

  const breadcrumbs = [
    { name: 'My Drive', path: '' },
    ...currentPath.split('/').filter(Boolean).map((segment, index, array) => ({
      name: toTitleCase(segment),
      path: array.slice(0, index + 1).join('/')
    }))
  ]

  const formatSize = (size?: number) => {
    if (!size) return '-'
    const units = ['B', 'KB', 'MB', 'GB']
    let value = size
    let unitIndex = 0
    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024
      unitIndex++
    }
    return `${value.toFixed(1)} ${units[unitIndex]}`
  }

  const isFolder = (item: DriveItem) => !item.size
  const isImage = (item: DriveItem) => 
    item.mimeType?.startsWith('image/') || 
    /\.(jpg|jpeg|png|gif|webp)$/i.test(item.name)
  const isHidden = (item: DriveItem) =>
    item.name.startsWith('.') || item.name === '.keep'

  const handleContextMenu = (e: React.MouseEvent, item: DriveItem) => {
    e.preventDefault()
    setSelectedItems([item])
  }

  const visibleItems = items.filter(item => !isHidden(item))

  return (
    <div className="space-y-4">
      {/* Breadcrumb Navigation */}
      <div className="flex items-center gap-2 bg-muted/30 p-2 rounded-md">
        {breadcrumbs.map((crumb, index) => (
          <div key={crumb.path} className="flex items-center">
            {index > 0 && <ChevronRight className="h-4 w-4 text-muted-foreground mx-1" />}
            <Button
              variant="ghost"
              className="h-8 px-2 font-medium"
              onClick={() => onPathChange(crumb.path)}
            >
              {crumb.name}
            </Button>
          </div>
        ))}
      </div>

      {/* View Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant={view === 'grid' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => onViewChange('grid')}
          >
            <Grid className="h-4 w-4" />
          </Button>
          <Button
            variant={view === 'list' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => onViewChange('list')}
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* File Grid/List View */}
      <div className={view === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4' : 'space-y-2'}>
        {/* Folders First */}
        {visibleItems.filter(isFolder).map((item) => (
          <div
            key={item.id}
            className={cn(
              "relative group cursor-pointer rounded-lg border p-3",
              view === 'grid' ? 'flex flex-col items-center' : 'flex items-center justify-between',
              "hover:bg-accent hover:shadow-md transition-all"
            )}
            onClick={() => onItemClick(item)}
            onContextMenu={(e) => handleContextMenu(e, item)}
          >
            <div className={cn("flex items-center gap-3", view === 'grid' && "flex-col")}>
              <Folder className="h-12 w-12 text-blue-500" />
              <div className={view === 'grid' ? 'text-center' : ''}>
                <div className="font-medium">{toTitleCase(item.name)}</div>
                {view === 'list' && (
                  <div className="text-sm text-muted-foreground">
                    Modified {item.updated_at ? 
                      formatDistanceToNow(new Date(item.updated_at), { addSuffix: true }) : '-'}
                  </div>
                )}
              </div>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 absolute top-2 right-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {onGetDetails && (
                  <DropdownMenuItem onClick={(e) => {
                    e.stopPropagation()
                    onGetDetails(item)
                  }}>
                    Details
                  </DropdownMenuItem>
                )}
                {onMove && (
                  <DropdownMenuItem onClick={(e) => {
                    e.stopPropagation()
                    onMove(item)
                  }}>
                    Move
                  </DropdownMenuItem>
                )}
                {onCreateFolder && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={(e) => {
                      e.stopPropagation()
                      onCreateFolder()
                    }}>
                      New Folder
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ))}

        {/* Then Files */}
        {visibleItems.filter(item => !isFolder(item)).map((item) => (
          <div
            key={item.id}
            className={cn(
              "relative group cursor-pointer rounded-lg border",
              view === 'grid' ? 'flex flex-col' : 'flex items-center justify-between',
              "hover:bg-accent/50 hover:shadow-sm transition-all"
            )}
            onClick={() => onItemClick(item)}
            onContextMenu={(e) => handleContextMenu(e, item)}
          >
            {isImage(item) && item.url ? (
              <div className={cn(
                "relative overflow-hidden",
                view === 'grid' ? "w-full aspect-video rounded-t-lg" : "w-16 h-16 rounded-l-lg"
              )}>
                <img
                  src={item.url}
                  alt={item.name}
                  className="object-cover w-full h-full"
                />
              </div>
            ) : (
              <div className={cn(
                "flex items-center justify-center",
                view === 'grid' ? "w-full aspect-video rounded-t-lg bg-muted/30" : "w-16 h-16"
              )}>
                {isImage(item) ? (
                  <ImageIcon className="h-8 w-8 text-muted-foreground" />
                ) : (
                  <File className="h-8 w-8 text-muted-foreground" />
                )}
              </div>
            )}

            <div className={cn(
              "flex-1 min-w-0",
              view === 'grid' ? "p-3 w-full" : "p-3 flex items-center justify-between flex-1"
            )}>
              <div>
                <div className="font-medium truncate">{toTitleCase(item.name)}</div>
                <div className="text-sm text-muted-foreground">
                  {formatSize(item.size)} • Modified {item.updated_at ? 
                    formatDistanceToNow(new Date(item.updated_at), { addSuffix: true }) : '-'}
                </div>
              </div>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 absolute top-2 right-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {onGetDetails && (
                    <DropdownMenuItem onClick={(e) => {
                      e.stopPropagation()
                      onGetDetails(item)
                    }}>
                      Details
                    </DropdownMenuItem>
                  )}
                  {onMove && (
                    <DropdownMenuItem onClick={(e) => {
                      e.stopPropagation()
                      onMove(item)
                    }}>
                      Move
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
